<template>
  <Tray.Root
    v-slot="{ isCompact }"
    :open="selection !== undefined"
    :wrap="wrappable"
  >
    <div
      class="mr-4 flex items-center gap-4 pl-2"
      :class="[!isCompact ? `h-8 shrink-0` : `flex-wrap`]"
    >
      <Forms.Checkbox
        v-model="isFullySelected"
        :indeterminate="isIndeterminate"
      >
        <span class="whitespace-nowrap">{{ label }}</span>
      </Forms.Checkbox>

      <button
        v-if="
          selection?.mode === 'some' &&
          selection.items.length === items.length &&
          totalCount !== undefined &&
          items.length < totalCount
        "
        type="button"
        class="text-blue-500 hover:underline"
        @click="selection = { mode: 'all_except', items: [] }"
      >
        Select all {{ totalCount }} {{ noun(totalCount) }}
      </button>
    </div>

    <slot name="extra-actions"></slot>

    <Tray.Actions :actions="actions" />
  </Tray.Root>
</template>

<script setup lang="ts" generic="T">
import { computed, toValue, unref } from "vue";

import Forms from "@/design_system/Form";
import Tray from "@/design_system/Tray";
import { useKeybinds } from "@/lib/hotkeys";
import type { SelectionOptions } from "@/lib/tanstack-table";
import { useSelectionCheckbox } from "@/lib/tanstack-table/selection-helpers";
import type { Selection } from "@/types/selection";

const selection = defineModel<Selection<T>>();

const props = defineProps<{
  definition: NonNullable<SelectionOptions<T>["widget"]>;
  items: T[];
  totalCount?: number;
  wrappable?: boolean;
  onCanSelect?: (item: T, state: Selection<T> | undefined) => boolean;
}>();

const { isFullySelected, isIndeterminate } = useSelectionCheckbox({
  state: selection,
  items: () => props.items,
  totalCount: () => props.totalCount,
  canSelect: props.onCanSelect,
});

const actions = computed(() => {
  const raw = unref(props.definition.actions);
  if (typeof raw === "function") {
    const current = selection.value;
    if (!current) {
      return [];
    }

    return raw(current);
  }

  return raw;
});

const noun = (count: number) => {
  const labels = toValue(props.definition.labels);
  return count === 1 ? labels.one : labels.many;
};

const label = computed((): string | null => {
  const current = selection.value;
  if (!current) {
    return null;
  }

  switch (current.mode) {
    case "some": {
      const including = current.items.length;

      return `${including} ${noun(including)} selected`;
    }
    case "all_except": {
      const total = props.totalCount ?? props.items.length;
      const excluding = current.items.length;
      const remaining = total - excluding;

      if (excluding === 0) {
        return `All ${total} ${noun(total)} selected`;
      } else {
        return `${remaining} ${noun(
          remaining
        )} selected (${excluding} excluded)`;
      }
    }
  }

  return null;
});

useKeybinds({
  Escape: {
    name: `Unselect all items`,
    group: `Selection`,
    hidden: computed(() => {
      return selection.value === undefined;
    }),
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      const current = selection.value;
      if (!current) {
        return;
      }

      selection.value = undefined;
      return true;
    },
  },
  "$mod+a": {
    name: `Select all items`,
    group: `Selection`,
    hidden: isFullySelected,
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      isFullySelected.value = true;
      return true;
    },
  },
});
</script>
